import BelleJS from 'belle-js'
import superagent from 'superagent'
import { onboardingToken, secureDocUploaderToken } from './tokens'
import { TokenValidationError, InternalServerError } from 'utils/errorTypes'
import {
  PROSPECT_PATH,
  FINANCIAL_ACCOUNTS_PATH,
  UPDATE_FINANCIAL_ACCOUNT_PATH,
  GET_JOINT_OWNER_PATH,
  UPDATE_JOINT_OWNER_PATH,
  CONTACTS_PATH,
  POST_FINANCIAL_ACCOUNT_ROLE_PATH,
  CONTACT_FINANCIAL_ACCOUNT_ROLES_PATH,
  CONTACT_FINANCIAL_ACCOUNT_ROLE_PATH,
  RECIPROCAL_ROLES_PATH,
  CONTACT_TYPES_PATH,
  CONTACT_PATH
} from './apiPaths'

const { traveller } = BelleJS
export const apiHost = `${process.env.REACT_APP_API_HOST}/v1/prospect`

const renewSession = (response) => {
  window.sessionHandler.renewSession()
  return response
}

const handleApiErrorResponse = (feature, error) => {
  if (error && error.response) {
    if (error.response.status === 401) {
      throw new TokenValidationError(feature)
    } else {
      throw new InternalServerError()
    }
  }
  throw error
}

const travellerApiRequest = (feature, endpoint, method, token, options) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }

  const allOptions = { ...options, headers }
  const response = traveller(`${apiHost}${endpoint}`, method, false, allOptions)
    .then(renewSession)
    .catch(e => handleApiErrorResponse(feature, e))
  return response
}

const superagentApiRequest = (feature, endpoint, method, token, file) => (
  superagent(method, `${apiHost}${endpoint}`)
    .set('Authorization', `Bearer ${token}`)
    .attach('file', file)
    .then(renewSession)
    .catch((e) => { handleApiErrorResponse(feature, e) })
)

const onboardingApiRequest = (endpoint, method, token, options) => (
  travellerApiRequest('onboarding', endpoint, method, token, options)
)

const secureDocUploaderApiRequest = (endpoint, method, token, options) => (
  superagentApiRequest('secure_doc_uploader', endpoint, method, token, options)
)

/* General */
export const validateToken = (token, feature) => {
  const tokenPath = `/token_validation?feature=${feature}`
  return travellerApiRequest(feature, tokenPath, 'GET', token)
}

/* Onboarding */
export const getProspect = (token) => {
  const authToken = token || onboardingToken()
  return onboardingApiRequest(PROSPECT_PATH, 'GET', authToken)
}

export const createContactFinancialAccountRole = (params, token) => {
  const authToken = token || onboardingToken()
  return onboardingApiRequest(POST_FINANCIAL_ACCOUNT_ROLE_PATH, 'POST', authToken, {
    body: JSON.stringify({
      contact_financial_account_role: params
    })
  })
}

export const getFinancialAccounts = (token) => {
  const authToken = token || onboardingToken()
  return onboardingApiRequest(FINANCIAL_ACCOUNTS_PATH, 'GET', authToken)
}

export const getContacts = (token) => {
  const authToken = token || onboardingToken()
  return onboardingApiRequest(CONTACTS_PATH, 'GET', authToken)
}

export const updateProspect = (data, token) => {
  const authToken = token || onboardingToken()
  return onboardingApiRequest(PROSPECT_PATH, 'PUT', authToken, {
    body: JSON.stringify({ account: data })
  })
}

export const createContact = (data) => {
  const authToken = onboardingToken()

  return onboardingApiRequest(CONTACTS_PATH, 'POST', authToken, {
    body: JSON.stringify(data)
  })
}

export const updateContact = (id, data, token) => {
  const authToken = token || onboardingToken()
  const path = CONTACT_PATH.replace(':contactId', id)
  return onboardingApiRequest(path, 'PUT', authToken, {
    body: JSON.stringify({ contact: data })
  })
}

export const getJointOwners = (financialAccountId, token) => {
  const authToken = token || onboardingToken()
  const parsedPath = GET_JOINT_OWNER_PATH.replace(':financialAccountId', financialAccountId)
  return onboardingApiRequest(parsedPath, 'GET', authToken)
}

export const updateJointOwner = (financialAccountId, jointOwnerId, data, token) => {
  const authToken = token || onboardingToken()
  const parsedPath = UPDATE_JOINT_OWNER_PATH
    .replace(':financialAccountId', financialAccountId)
    .replace(':jointOwnerId', jointOwnerId)
  return onboardingApiRequest(parsedPath, 'PUT', authToken, {
    body: JSON.stringify({ joint_owner: data })
  })
}

export const updateFinancialAccount = (financialAccountId, data, token) => {
  const authToken = token || onboardingToken()
  const parsedPath = UPDATE_FINANCIAL_ACCOUNT_PATH
    .replace(':financialAccountId', financialAccountId)

  return onboardingApiRequest(parsedPath, 'PUT', authToken, {
    body: JSON.stringify({ financial_account: data })
  })
}

export const createOnboardingCompletion = (token) => {
  const authToken = token || onboardingToken()
  const completionsPath = '/onboarding_completions'
  return onboardingApiRequest(completionsPath, 'POST', authToken)
}

/* Secure Document Uploader */
export const uploadFile = (file, token) => {
  const authToken = token || secureDocUploaderToken()
  return secureDocUploaderApiRequest('/files', 'POST', authToken, file)
}

export const getContactFinancialAccountRoles = (token) => {
  const authToken = token || onboardingToken()

  return onboardingApiRequest(CONTACT_FINANCIAL_ACCOUNT_ROLES_PATH, 'GET', authToken)
}

export const updateContactFinancialAccountRole = (contactFinancialAccountRoleId, data) => {
  const authToken = onboardingToken()
  const financialAccountRoleUrl = CONTACT_FINANCIAL_ACCOUNT_ROLE_PATH
    .replace(':contactFinancialAccountRoleId', contactFinancialAccountRoleId)
  return onboardingApiRequest(financialAccountRoleUrl, 'PUT', authToken, {
    body: JSON.stringify({
      contact_financial_account_role: data
    })
  })
}

export const deleteContactFinancialAccountRole = (id, token) => {
  const authToken = token || onboardingToken()
  const financialAccountRoleUrl = CONTACT_FINANCIAL_ACCOUNT_ROLE_PATH
    .replace(':contactFinancialAccountRoleId', id)

  return onboardingApiRequest(financialAccountRoleUrl, 'DELETE', authToken)
}

export const getReciprocalRoles = (token) => {
  const authToken = token || onboardingToken()

  return onboardingApiRequest(RECIPROCAL_ROLES_PATH, 'GET', authToken)
}

export const createContactRelationship = (data, token) => {
  const authToken = onboardingToken()

  return onboardingApiRequest('/contact_relationships', 'POST', authToken, {
    body: JSON.stringify({
      contact_relationship: data
    })
  })
}

export const getContactRelationships = (token) => {
  const authToken = token || onboardingToken()

  return onboardingApiRequest('/contact_relationships', 'GET', authToken)
}

export const deleteContactRelationship = (id, token) => {
  const authToken = token || onboardingToken()

  return onboardingApiRequest(`/contact_relationships/${id}`, 'DELETE', authToken)
}


export const getContactTypes = (token) => {
  const authToken = token || onboardingToken()

  return onboardingApiRequest(CONTACT_TYPES_PATH, 'GET', authToken)
}

export default travellerApiRequest



// WEBPACK FOOTER //
// ./src/utils/apiRequest.js